const VERSION_CHECK_INTERVAL_MS = 60_000;

type VersionManifest = {
  version: string;
};

function isVersionManifest(value: unknown): value is VersionManifest {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const version = (value as Record<string, unknown>).version;
  return typeof version === "string" && version.trim().length > 0;
}

export async function fetchDeployedVersion(
  fetchImplementation: typeof fetch = fetch,
): Promise<string> {
  const response = await fetchImplementation(
    `${import.meta.env.BASE_URL}version.json`,
    { cache: "no-store" },
  );

  if (!response.ok) {
    throw new Error(`Version check failed with status ${response.status}.`);
  }

  const manifest: unknown = await response.json();
  if (!isVersionManifest(manifest)) {
    throw new Error("Version check returned an invalid manifest.");
  }

  return manifest.version.trim();
}

type ReleaseVersionCheckerOptions = {
  currentVersion: string;
  fetchVersion?: () => Promise<string>;
  now?: () => number;
  minimumIntervalMs?: number;
};

export class ReleaseVersionChecker {
  private readonly currentVersion: string;
  private readonly fetchVersion: () => Promise<string>;
  private readonly now: () => number;
  private readonly minimumIntervalMs: number;
  private lastCheckedAt: number | null = null;
  private inFlightCheck: Promise<boolean> | null = null;

  constructor({
    currentVersion,
    fetchVersion = () => fetchDeployedVersion(),
    now = Date.now,
    minimumIntervalMs = VERSION_CHECK_INTERVAL_MS,
  }: ReleaseVersionCheckerOptions) {
    this.currentVersion = currentVersion.trim();
    this.fetchVersion = fetchVersion;
    this.now = now;
    this.minimumIntervalMs = minimumIntervalMs;
  }

  checkForUpdate(): Promise<boolean> {
    if (!this.currentVersion) {
      return Promise.resolve(false);
    }

    if (this.inFlightCheck) {
      return this.inFlightCheck;
    }

    const checkedAt = this.now();
    if (
      this.lastCheckedAt !== null &&
      checkedAt - this.lastCheckedAt < this.minimumIntervalMs
    ) {
      return Promise.resolve(false);
    }

    this.lastCheckedAt = checkedAt;
    this.inFlightCheck = this.fetchVersion()
      .then((deployedVersion) => deployedVersion !== this.currentVersion)
      .finally(() => {
        this.inFlightCheck = null;
      });

    return this.inFlightCheck;
  }
}
